import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Link,
  Image,
} from "@react-pdf/renderer";
import ResumeDataHookProps from "../props/ResumeDataHook.props";
import {
  faEnvelope,
  faPhoneAlt,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Path, Svg } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
import {
  LSLayout as LSLayoutProps,
  RSLayout as RSLayoutProps,
} from "../props/State.props";

interface FontAwesomeIconProps {
  faIcon: IconDefinition;
  style?: Style;
}

const FontAwesomeIcon = ({
  faIcon: { icon },
  style = {},
}: FontAwesomeIconProps) => {
  const duotone = Array.isArray(icon[4]);
  const paths = Array.isArray(icon[4]) ? icon[4] : [icon[4]];
  const color = style.color || "black";
  return (
    <Svg viewBox={`0 0 ${icon[0]} ${icon[1]}`} style={style}>
      {paths &&
        paths.map((d, index) => (
          <Path
            d={d}
            key={index}
            fill={color}
            fillOpacity={duotone && index === 0 ? 0.4 : 1.0}
          />
        ))}
    </Svg>
  );
};

Font.register({
  family: "Montserrat",
  format: "truetype",
  fonts: [
    { src: "/fonts/Montserrat/light.ttf", fontWeight: 300 },
    { src: "/fonts/Montserrat/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Montserrat/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Montserrat/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Montserrat/bold.ttf", fontWeight: 700 },
  ],
});

Font.register({
  family: "Inter",
  format: "truetype",
  fonts: [
    { src: "/fonts/Inter/light.ttf", fontWeight: 300 },
    { src: "/fonts/Inter/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Inter/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Inter/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Inter/bold.ttf", fontWeight: 700 },
  ],
});

Font.register({
  family: "Rubik",
  format: "truetype",
  fonts: [
    { src: "/fonts/Rubik/light.ttf", fontWeight: 300 },
    { src: "/fonts/Rubik/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Rubik/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Rubik/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Rubik/bold.ttf", fontWeight: 700 },
  ],
});

Font.register({
  family: "Poppins",
  format: "truetype",
  fonts: [
    { src: "/fonts/Poppins/light.ttf", fontWeight: 300 },
    { src: "/fonts/Poppins/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Poppins/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Poppins/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Poppins/bold.ttf", fontWeight: 700 },
  ],
});

Font.register({
  family: "Manrope",
  format: "truetype",
  fonts: [
    { src: "/fonts/Manrope/light.ttf", fontWeight: 300 },
    { src: "/fonts/Manrope/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Manrope/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Manrope/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Manrope/bold.ttf", fontWeight: 700 },
  ],
});

Font.register({
  family: "Roboto",
  format: "truetype",
  fonts: [
    { src: "/fonts/Roboto/light.ttf", fontWeight: 300 },
    { src: "/fonts/Roboto/regular.ttf", fontWeight: 400 },
    { src: "/fonts/Roboto/medium.ttf", fontWeight: 500 },
    { src: "/fonts/Roboto/semibold.ttf", fontWeight: 600 },
    { src: "/fonts/Roboto/bold.ttf", fontWeight: 700 },
  ],
});

const Preview = ({
  resumeData,
  date,
  theme,
  font,
}: {
  resumeData: ResumeDataHookProps[0];
  date: Date;
  theme: { [key: string]: string | undefined };
  font: string;
}) => {
  const name = `${resumeData.name.replace(/ /gi, "_")}_${date
    .toDateString()
    .replace(/ /gi, "_")}_${date.getTime()}`;

  const styles = StyleSheet.create({
    page: {
      flexDirection: "row",
      padding: "20px 25px",
      backgroundColor: theme.bg,
      color: theme.fg,
      fontFamily: font,
      margin: 0,
    },
    icon: {
      width: "10px",
      color: theme.fg,
    },
    row: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    by2: {
      width: "50%",
    },
    name: {
      fontSize: "17px",
      fontWeight: 700,
      color: theme.fg,
    },
    position: {
      fontSize: "12px",
      fontWeight: 500,
      color: theme.fg,
    },
    description: {
      fontSize: "10px",
      fontWeight: 500,
      color: theme.fg,
      marginTop: "5px",
      marginBottom: "5px",
    },
    hr: {
      width: "100%",
      height: "0.3px",
      backgroundColor: theme.fg,
      margin: "10px 0px",
    },
    header: {
      fontSize: "15px",
      fontWeight: 700,
      color: theme.fg,
    },
    company: {
      marginTop: "9px",
      fontSize: "13px",
      fontWeight: 700,
    },
    role: {
      marginTop: "4px",
      fontSize: "9px",
      fontWeight: 500,
    },
    date: {
      marginTop: "4px",
      fontSize: "8px",
      fontWeight: 400,
    },
    responsibility: {
      marginBottom: "8px",
      fontSize: "10px",
      fontWeight: 400,
    },
  });

  const RenderIntro = () => {
    return (
      <View style={styles.row}>
        <View style={{ ...styles.by2, paddingRight: "10px" }}>
          <View
            style={{
              ...styles.row,
              justifyContent: "flex-start",
              marginBottom: "5px",
            }}
          >
            <Image
              style={{
                width: "60px",
                height: "60px",
                marginRight: "10px",
                borderRadius: "50%",
              }}
              src={resumeData.dp}
            />
            <View>
              <Text style={styles.name}>{resumeData.name}</Text>
              <Text style={styles.position}>{resumeData.position}</Text>
            </View>
          </View>
          <Text style={styles.description}>{resumeData.description}</Text>
        </View>
        <View
          style={{ ...styles.by2, paddingLeft: "10px", marginTop: "-10px" }}
        >
          <View style={{ ...styles.row, justifyContent: "flex-end" }}>
            <Text style={{ ...styles.description, marginRight: "10px" }}>
              {resumeData.email}
            </Text>
            <FontAwesomeIcon faIcon={faEnvelope} style={styles.icon} />
          </View>
          <View style={{ ...styles.row, justifyContent: "flex-end" }}>
            <Text style={{ ...styles.description, marginRight: "10px" }}>
              {resumeData.ph_no}
            </Text>
            <FontAwesomeIcon faIcon={faPhoneAlt} style={styles.icon} />
          </View>
          <View style={{ ...styles.row, justifyContent: "flex-end" }}>
            <Text style={{ ...styles.description, marginRight: "10px" }}>
              {resumeData.location}
            </Text>
            <FontAwesomeIcon faIcon={faLocationDot} style={styles.icon} />
          </View>
          <View
            style={{
              ...styles.row,
              justifyContent: "flex-end",
            }}
          >
            <Link
              src={resumeData.linkedin}
              style={{
                ...styles.description,
                marginRight: "10px",
              }}
            >
              {resumeData.linkedin}
            </Link>
            <FontAwesomeIcon faIcon={faLinkedin} style={styles.icon} />
          </View>
        </View>
      </View>
    );
  };

  const RenderColumns = () => {
    return (
      <View style={styles.row}>
        <View
          style={{
            ...styles.by2,
            paddingRight: "20px",
          }}
        >
          {resumeData.lsLayout.map((data) => {
            return (
              <>
                {data.type === "exp" && <RenderWorkExp exp={data} />}
                {data.type === "ed" && <RenderEd ed={data} />}
                {data.type === "lang" && <RenderLang lang={data} />}
              </>
            );
          })}
        </View>
        <View style={{ ...styles.by2, paddingLeft: "20px" }}>
          {resumeData.rsLayout.map((data) => {
            return (
              <>
                {data.type === "skill" && <RenderSkills skill={data} />}
                {data.type === "cert" && <RenderCertificates cert={data} />}
              </>
            );
          })}
        </View>
      </View>
    );
  };

  const RenderWorkExp = ({ exp }: { exp: LSLayoutProps }) => {
    return (
      <View style={{ width: "100%", marginTop: "10px" }}>
        <Text style={styles.header}>{exp.title}</Text>
        <View style={{ margin: "8px 0px" }}>
          {exp.record.map((e) => {
            return (
              <View style={{ width: "100%" }}>
                <Text style={styles.company}>{e.company}</Text>
                <Text style={styles.role}>{e.role}</Text>
                <View style={styles.row}>
                  <Text style={styles.date}>{e.date}</Text>
                  <Text style={{ ...styles.date }}>{e.location}</Text>
                </View>
                <View style={{ marginTop: "10px", marginBottom: "5px" }}>
                  {e.responsibilities?.map((r) => (
                    <Text style={styles.responsibility}>- {r}</Text>
                  ))}
                </View>
              </View>
            );
          })}
        </View>
        <View style={{ ...styles.hr, marginTop: "3px" }} />
      </View>
    );
  };

  const RenderEd = ({ ed }: { ed: LSLayoutProps }) => {
    return (
      <View style={{ width: "100%", marginTop: "10px" }}>
        <Text style={styles.header}>{ed.title}</Text>
        <View style={{ margin: "8px 0px" }}>
          {ed.record.map((e) => {
            return (
              <View style={{ width: "100%" }}>
                <Text style={styles.company}>{e.degree}</Text>
                <Text style={styles.role}>{e.university}</Text>
                <Text style={{ ...styles.date, marginBottom: "5px" }}>
                  {e.date}
                </Text>
              </View>
            );
          })}
        </View>
        <View style={{ ...styles.hr, marginTop: "3px" }} />
      </View>
    );
  };

  const RenderLang = ({ lang }: { lang: LSLayoutProps }) => {
    return (
      <View style={{ width: "100%", marginTop: "10px" }}>
        <Text style={styles.header}>{lang.title}</Text>
        <View style={{ margin: "8px 0px" }}>
          {lang.record.map((e) => {
            return (
              <View
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Text style={{ ...styles.role, marginBottom: "5px" }}>
                  {e.lang}
                </Text>
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginLeft: "30px",
                    width: "65%",
                  }}
                >
                  {[1, 2, 3, 4, 5].map((l) => (
                    <View
                      style={{
                        width: "7px",
                        height: "7px",
                        backgroundColor: l <= e.rate! ? theme.fg : theme.bg,
                        borderRadius: "70px",
                        border: `1px solid ${theme.fg}`,
                        marginRight: "10px",
                      }}
                    />
                  ))}
                </View>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  const RenderSkills = ({ skill }: { skill: RSLayoutProps }) => {
    return (
      <View style={{ width: "100%", marginTop: "15px" }}>
        <Text style={styles.header}>{skill.title}</Text>
        <View
          style={{
            ...styles.row,
            flexWrap: "wrap",
            margin: "20px 0px 10px 0px",
            gap: "5px",
            justifyContent: "flex-start",
          }}
        >
          {skill.record.skills!.map((e) => {
            return (
              <View
                style={{
                  backgroundColor: theme.fg,
                  color: theme.bg,
                  padding: "6px",
                  borderRadius: "5px",
                  fontSize: "10px",
                  width: "auto",
                }}
              >
                <Text>{e}</Text>
              </View>
            );
          })}
        </View>
        <View style={styles.hr} />
      </View>
    );
  };

  const RenderCertificates = ({ cert }: { cert: RSLayoutProps }) => {
    return (
      <View style={{ width: "100%", marginTop: "10px" }}>
        <Text style={styles.header}>{cert.title}</Text>
        <View
          style={{
            marginTop: "10px",
            fontSize: "10px",
          }}
        >
          {cert.record.certifications!.map((e) => {
            return (
              <Link src={e} style={{ color: theme.fg, marginBottom: "15px" }}>
                {e}
              </Link>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <Document
      title={name}
      author="varunpbardwaj"
      subject="resume_builder"
      creator={"reesume"}
    >
      <Page size="A4" style={styles.page}>
        <View>
          <RenderIntro />
          <View style={styles.hr} />
          <RenderColumns />
        </View>
      </Page>
    </Document>
  );
};

export default Preview;
